import { useEffect, useState } from "react";
import { X, Ruler } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * SizeGuideModal — pulls Printful's official size chart for the given product
 * (measure-yourself diagram + product measurement table). Zero configuration
 * per product — the size guide auto-updates whenever Printful updates theirs.
 */
export default function SizeGuideModal({ productId, productName, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(""); setData(null);
    fetch(`${BACKEND}/api/shop/products/${productId}/size-guide`)
      .then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        return j;
      })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message || "Size chart unavailable"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [productId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const tables = data?.size_tables || [];
  const measureYou = tables.find((t) => t.type === "measure_yourself");
  const productMeasure = tables.find((t) => t.type === "product_measure");

  return (
    <div data-testid="size-guide-modal" className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0F0F0F] border-2 border-white/15 w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-white/10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027] flex items-center gap-2 mb-1">
              <Ruler size={12} weight="bold" /> Size guide
            </p>
            <h2 className="font-display font-black uppercase tracking-tight text-white text-lg leading-tight">{productName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close size guide"
            data-testid="size-guide-close"
            className="text-white/60 hover:text-white p-1"
          >
            <X size={24} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 text-white/85">
          {loading && <p className="text-white/50 text-sm">Loading size chart…</p>}
          {err && (
            <div className="border border-yellow-500/50 bg-yellow-500/10 p-4 text-sm text-yellow-200">
              Size chart unavailable ({err}). Standard unisex sizing applies — reach out at <a href="mailto:Admin@sweatwithslay.com" className="underline">Admin@sweatwithslay.com</a> if you need help choosing.
            </div>
          )}

          {data && data.available_sizes?.length > 0 && (
            <div className="mb-5">
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60 mb-2">Available sizes</p>
              <div className="flex flex-wrap gap-1.5">
                {data.available_sizes.map((s) => (
                  <span key={s} className="px-2 py-1 border border-white/20 text-white text-xs font-bold">{s}</span>
                ))}
              </div>
            </div>
          )}

          {measureYou && (
            <div className="mb-8">
              <h3 className="font-display font-black uppercase text-white text-base tracking-tight mb-2">How to measure yourself</h3>
              {measureYou.image_url && (
                <img
                  src={measureYou.image_url}
                  alt="How to measure yourself"
                  loading="lazy"
                  className="w-full max-w-md border border-white/10 bg-white mb-3"
                />
              )}
              {measureYou.description && (
                <div
                  className="text-white/70 text-sm leading-relaxed size-guide-html"
                  dangerouslySetInnerHTML={{ __html: measureYou.description }}
                />
              )}
            </div>
          )}

          {productMeasure && (
            <div>
              <h3 className="font-display font-black uppercase text-white text-base tracking-tight mb-2">Product measurements</h3>
              {productMeasure.image_url && (
                <img
                  src={productMeasure.image_url}
                  alt="Product measurements"
                  loading="lazy"
                  className="w-full max-w-md border border-white/10 bg-white mb-3"
                />
              )}
              {productMeasure.description && (
                <div
                  className="text-white/70 text-sm leading-relaxed size-guide-html"
                  dangerouslySetInnerHTML={{ __html: productMeasure.description }}
                />
              )}
              {productMeasure.measurements?.length > 0 && (
                <div className="overflow-x-auto mt-3 border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-[#0A0A0A]">
                      <tr>
                        <th className="text-left px-3 py-2 uppercase tracking-[0.2em] text-[10px] text-white/60 font-bold border-r border-white/10">Type</th>
                        {productMeasure.measurements[0].values?.map((v) => (
                          <th key={v.size} className="px-3 py-2 uppercase tracking-[0.2em] text-[10px] text-white/60 font-bold">{v.size}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {productMeasure.measurements.map((m) => (
                        <tr key={m.type_label} className="border-t border-white/10">
                          <td className="px-3 py-2 font-semibold text-white border-r border-white/10">{m.type_label}</td>
                          {m.values?.map((v) => (
                            <td key={v.size} className="px-3 py-2 text-white/70 text-center">
                              {v.value ? `${v.value}${m.unit || ""}` : (v.min_value && v.max_value ? `${v.min_value}–${v.max_value}${m.unit || ""}` : "—")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 text-right">
          <button
            type="button"
            onClick={onClose}
            data-testid="size-guide-done"
            className="text-[10px] uppercase tracking-[0.25em] font-bold px-5 py-2 bg-[#B32027] hover:bg-[#D42730] text-white"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
